import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import Slider, { Settings as SlickConfig } from 'react-slick';
import CarouselProps from './type';
import { SvgImage, Link } from '../common';
import '../../styles/vendor/slick-caroursel.scss';
import './styles.scss';

const Carousel = ({
  title,
  children,
  slickConfig,
  large,
  containerTheme,
  viewMoreLink,
  controlInline,
}: CarouselProps) => {
  const slider = useRef<Slider>(null);
  const sliderBody = useRef<HTMLDivElement>(null);
  const [hasSetPosition, setHasSetPosition] = useState(false); // fix for initialSlide !== 0
  const [nextIsDisabled, setNextIsDisabled] = useState<boolean>(true);
  const [previousIsDisabled, setPreviousIsDisabled] = useState<boolean>(true);
  const [arrowsAreHidden, setArrowsAreHidden] = useState<boolean>(false);

  /**
   * Checks for the disabled slick controls in order to set
   * the state accordingly to enable/disable custom next/previous
   * buttons.
   */
  const checkSlickDisabled = () => {
    const slickNextButton = sliderBody.current?.querySelector('.slick-next') as HTMLButtonElement;
    const slickPreviousButton = sliderBody.current?.querySelector('.slick-prev') as HTMLButtonElement;
    if (slickNextButton) {
      setNextIsDisabled(
        slickNextButton.classList.contains('slick-disabled'),
      );
    }

    if (slickPreviousButton) {
      setPreviousIsDisabled(
        slickPreviousButton.classList.contains('slick-disabled'),
      );
    }
  };

  // Default slick settings
  const defaultSlickSettings: SlickConfig = {
    accessibility: true,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 5.15,
    slidesToScroll: 1,
    initialSlide: 0,
    arrows: true,
    useTransform: false,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 4.1,
          arrows: true,
        },
      },
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 3.5,
          arrows: true,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3.1,
          arrows: true,
        },
      },
      {
        breakpoint: 950,
        settings: {
          slidesToShow: 2.5,
          arrows: true,
        },
      },
      {
        breakpoint: 750,
        settings: {
          slidesToShow: 2.1,
          arrows: true,
        },
      },
      {
        breakpoint: 550,
        settings: {
          slidesToShow: 1.5,
        },
      },
      {
        breakpoint: 400,
        settings: {
          arrows: false,
          slidesToShow: 1.1,
        },
      },
    ],
    onInit: () => {
      setTimeout(() => checkSlickDisabled(), 1500);
    },
    afterChange: () => {
      checkSlickDisabled();
    },
  };

  // Merge the any settings passed in via props with the default settings.
  const [settings] = useState<SlickConfig>(
    slickConfig ? Object.assign(defaultSlickSettings, slickConfig) : defaultSlickSettings,
  );

  useEffect(() => {
    checkSlickDisabled();
  }, []);

  useEffect(() => {
    if (previousIsDisabled && nextIsDisabled) {
      setArrowsAreHidden(true);
    } else {
      setArrowsAreHidden(false);
    }
  }, [previousIsDisabled, nextIsDisabled]);

  const paddleIcon = large
    ? '#icon-right-paddle-light'
    : '#icon-right-paddle';

  useEffect(() => {
    if (slider.current && !hasSetPosition) {
      slider.current?.slickGoTo(settings.initialSlide || 0);
      setHasSetPosition(true);
    }
  }, [settings.initialSlide, hasSetPosition, slider]);

  return (
    <div className={`carousel ${large ? 'carousel--large' : ''} ${containerTheme ? `carousel--${containerTheme}` : ''} ${controlInline ? 'carousel--inline' : ''}`}>
      <div className="carousel__header">
        {title && (
          <h3 className="carousel__title">{title}</h3>
        )}
        {!controlInline && (
          <div className="carousel__controls">
            <button
              type="button"
              className={`carousel__previous-slide ${arrowsAreHidden ? 'carousel__previous-slide--hidden' : ''}`}
              disabled={previousIsDisabled}
              onClick={() => slider.current?.slickPrev()}
              aria-label="previous"
            >
              <SvgImage ariaHidden reference={paddleIcon} />
            </button>
            <button
              type="button"
              className={`carousel__next-slide ${arrowsAreHidden ? 'carousel__next-slide--hidden' : ''}`}
              disabled={nextIsDisabled}
              onClick={() => slider.current?.slickNext()}
              aria-label="next"
            >
              <SvgImage ariaHidden reference={paddleIcon} />
            </button>
            {viewMoreLink && (
              <div className="carousel__view-more">
                <Link
                  {...viewMoreLink}
                  iconAfter={{ reference: '#icon-think-arrow', title: viewMoreLink.title }}
                  aria-label={viewMoreLink.title}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="carousel__body" ref={sliderBody}>
        <Slider {...settings} ref={slider}>
          {children}
        </Slider>
      </div>
      {controlInline && (
        <div className="carousel__controls">
          <button
            type="button"
            className={`carousel__previous-slide ${arrowsAreHidden ? 'carousel__previous-slide--hidden' : ''}`}
            disabled={previousIsDisabled}
            onClick={() => slider.current?.slickPrev()}
            aria-label="previous"
          >
            <SvgImage aria-hidden reference={paddleIcon} />
          </button>
          <button
            type="button"
            className={`carousel__next-slide ${arrowsAreHidden ? 'carousel__next-slide--hidden' : ''}`}
            disabled={nextIsDisabled}
            onClick={() => slider.current?.slickNext()}
            aria-label="next"
          >
            <SvgImage aria-hidden reference={paddleIcon} />
          </button>
          {viewMoreLink && (
            <div className="carousel__view-more">
              <Link
                {...viewMoreLink}
                iconAfter={{ reference: '#icon-think-arrow', title: viewMoreLink.title }}
                aria-label={viewMoreLink.title}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Carousel;
